import { ReactNode, forwardRef } from 'react';
import { useAuthNavigation } from '@/hooks/useAuthNavigation';

interface AuthLinkProps {
  portalKey: 'admin' | 'business-partners' | 'staff' | 'dashboard';
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  onSelect?: (event: Event) => void;
}

/**
 * Composant AuthLink pour gérer les liens vers les portails protégés
 * Gère automatiquement la redirection vers /login si non connecté
 * et vérifie les permissions de l'utilisateur
 */
export const AuthLink = forwardRef<HTMLAnchorElement, AuthLinkProps>(
  ({ portalKey, children, className, onClick, onSelect }, ref) => {
    const { navigateToPortal, loading } = useAuthNavigation();

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Ne rien faire si l'authentification est en cours de chargement
      if (loading) {
        console.log('⏳ Auth still loading, ignoring click');
        return;
      }

      // Appeler le callback onClick s'il existe
      onClick?.();

      // Naviguer vers le portail
      navigateToPortal(portalKey);
    };

    const handleSelect = (event: Event) => {
      // Ne rien faire si l'authentification est en cours de chargement
      if (loading) {
        console.log('⏳ Auth still loading, ignoring select');
        return;
      }

      // Appeler le callback onSelect s'il existe
      onSelect?.(event);

      // Appeler le callback onClick s'il existe
      onClick?.();

      // Naviguer vers le portail
      navigateToPortal(portalKey);
    };

    return (
      <a
        ref={ref}
        href="#"
        onClick={handleClick}
        onSelect={handleSelect as any}
        className={`${className || "cursor-pointer"} ${loading ? 'opacity-50 pointer-events-none' : ''}`}
        aria-disabled={loading}
      >
        {children}
      </a>
    );
  }
);

AuthLink.displayName = 'AuthLink';
